import { useHandler } from "@redotech/react-util/hook";
import { PageContext } from "@redotech/redo-agents-common/page-context";
import { useContext, useEffect } from "react";
import { GlobalAgentContext } from "../global-agent-context";

/**
 * Attaches this page to Ask Redo while mounted: publishes `pageContext` and
 * calls `reload` after the agent applies a change named in `reloadOn` (a
 * mutating tool name or an RPC name applied through execute_code).
 *
 * `pageContext` and `reloadOn` are compared by identity, so build them with
 * useMemo or hoist them to constants.
 */
export function useAskRedoPage({
  pageContext,
  reloadOn,
  reload,
}: {
  pageContext: PageContext | undefined;
  reloadOn: readonly string[];
  reload: () => void;
}): void {
  const { setPageContext, setOnPageDataChanged } =
    useContext(GlobalAgentContext);
  const handleReload = useHandler(reload);

  useEffect(() => {
    if (!pageContext) {
      return;
    }
    setPageContext(pageContext);
    return () => setPageContext(undefined);
  }, [setPageContext, pageContext]);

  useEffect(() => {
    setOnPageDataChanged((changed) => {
      if (changed.some((name) => reloadOn.includes(name))) {
        handleReload();
      }
    });
    return () => setOnPageDataChanged(undefined);
  }, [setOnPageDataChanged, handleReload, reloadOn]);
}
